/**
 * What the signed-in user's groups give them on each Odoo project, for the
 * « Lecture seule » badge (`ReadOnlyBadge.tsx`): `GET
 * /api/waadoo-projects/access` (backend `plugins/waadooProjects/
 * accessRoute.ts`, whose header holds the contract).
 *
 * UX ONLY: the badge says what the gated routes will refuse, it decides
 * nothing. Every route keeps its own rule.
 *
 * ONE REQUEST PER PAGE LOAD, shared by every badge of the screen (the sidebar,
 * the home page and the project or branch header are mounted at once), through
 * the `sharedLoad.ts` pattern with no expiry: the backend answers from the
 * ownership refs, frozen at sign-in, so the answer cannot change before the
 * next sign-in, which reloads the app. A failed request is not kept: the next
 * screen to mount asks again. The cache hangs off the app's `FetchApi`, as in
 * `useIsAdmin.ts`, so that each test starts from an empty one.
 */

import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import { createSharedLoad, type SharedLoad } from './sharedLoad';

/** The backend's answer, mirrored from `ProjectAccessResponse`. */
export interface ProjectAccessAnswer {
  admin: boolean;
  projects: Record<string, 'read' | 'edit'>;
}

/**
 * The caller's level on one project: `read` for a member of `prj-<p>` alone,
 * `edit` for an editor, `admin` for the admin tier, on every project.
 */
export type ProjectAccessLevel = 'read' | 'edit' | 'admin';

function isAnswer(body: unknown): body is ProjectAccessAnswer {
  if (typeof body !== 'object' || body === null) {
    return false;
  }
  const { admin, projects } = body as Record<string, unknown>;
  return (
    typeof admin === 'boolean' &&
    typeof projects === 'object' &&
    projects !== null &&
    Object.values(projects).every(level => level === 'read' || level === 'edit')
  );
}

export async function fetchProjectAccess(options: {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
}): Promise<ProjectAccessAnswer> {
  const baseUrl = await options.discoveryApi.getBaseUrl('waadoo-projects');
  const response = await options.fetchApi.fetch(`${baseUrl}/access`);
  if (!response.ok) {
    throw new Error(`Projects API: HTTP ${response.status}`);
  }
  const body: unknown = await response.json();
  if (!isAnswer(body)) {
    throw new Error('Projects API: unexpected access answer');
  }
  return body;
}

const loads = new WeakMap<FetchApi, SharedLoad<ProjectAccessAnswer>>();

/** The shared read behind {@link useProjectAccess}. Rejects when it fails. */
export function loadProjectAccess(options: {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
}): Promise<ProjectAccessAnswer> {
  let shared = loads.get(options.fetchApi);
  if (!shared) {
    shared = createSharedLoad<ProjectAccessAnswer>(Number.POSITIVE_INFINITY);
    loads.set(options.fetchApi, shared);
  }
  return shared.load(() => fetchProjectAccess(options));
}

/** The level of `project` in `answer`, undefined when the caller has none. */
export function accessLevel(
  answer: ProjectAccessAnswer,
  project: string,
): ProjectAccessLevel | undefined {
  if (answer.admin) {
    return 'admin';
  }
  return Object.prototype.hasOwnProperty.call(answer.projects, project)
    ? answer.projects[project]
    : undefined;
}

/**
 * The shared answer itself: undefined while it loads, null when the request
 * failed — for a screen that must tell « not known yet » from « no answer »
 * (the backups card waits for the first, and asks its own route on the
 * second).
 */
export function useProjectAccessAnswer():
  | ProjectAccessAnswer
  | null
  | undefined {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [answer, setAnswer] = useState<ProjectAccessAnswer | null>();

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const loaded = await loadProjectAccess({ fetchApi, discoveryApi });
        if (active) setAnswer(loaded);
      } catch {
        // Nothing shown: the badge is a hint, and a failure must not paint a
        // project read-only by mistake.
        if (active) setAnswer(null);
      }
    };
    // `load` turns its own failure into state, so nothing can reject.
    void load();
    return () => {
      active = false;
    };
  }, [fetchApi, discoveryApi]);

  return answer;
}

/**
 * The caller's level on a project, as a lookup: undefined while the answer
 * loads, when the request failed, and for a project the caller is in no group
 * of — so nothing is ever shown on a guess.
 */
export function useProjectAccess(): (
  project: string,
) => ProjectAccessLevel | undefined {
  const answer = useProjectAccessAnswer();
  return project => (answer ? accessLevel(answer, project) : undefined);
}
